/**
 * Salida Motor Taller — migración web de ares9020.
 *
 * Dos modos:
 *  - "Un motor": se busca la orden por chasis, se muestran sus gastos, se
 *    registra lo pagado por el cliente y el destino del vehículo (volver al
 *    inventario o devuelto a fábrica). Al confirmar se imprime el comprobante.
 *  - "Varios motores": lista todas las órdenes abiertas, se marcan con
 *    checkbox, se edita el pagado por cliente por orden y se les da salida a
 *    todas con un destino común en una sola transacción del backend
 *    (/api/taller_salida_multiple). Imprime un comprobante consolidado.
 */
import React, { useState, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import TwoWheelerIcon from "@mui/icons-material/TwoWheeler";
import LibraryAddCheckOutlinedIcon from "@mui/icons-material/LibraryAddCheckOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Autocomplete del filtro de órdenes abiertas: busca por chasis, marca o modelo.
const filtrarOpcionesOrdenes = createFilterOptions({
  stringify: (o) => (typeof o === "string" ? o : `${o.chasis} ${o.marca || ""} ${o.modelo || ""}`),
  limit: 60,
});

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function NoRowsOverlaySinCoincidencias() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin órdenes que coincidan con el filtro
      </Typography>
    </Stack>
  );
}

function NoRowsOverlaySinOrdenes() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin órdenes de taller abiertas
      </Typography>
    </Stack>
  );
}

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin gastos registrados en la orden
      </Typography>
    </Stack>
  );
}

// Comprobante de salida de taller (impresión con iframe oculto).
function imprimirSalida({ orden, totalgastos, pagadocliente, destino }) {
  const fila = (k, v) =>
    `<tr><td style="padding:3px 8px;color:#555">${k}</td><td style="padding:3px 8px;font-weight:bold">${
      v || ""
    }</td></tr>`;
  const html = `
    <html><head><meta name="color-scheme" content="light"><title>Salida de Taller #${
      orden.id
    }</title></head>
    <body style="background:#fff;color:#000;font-family:Arial,sans-serif;padding:18px;font-size:13px;max-width:420px;margin:0 auto">
      <p style="text-align:center;margin:0 0 4px;font-weight:bold">Salida de Taller</p>
      <p style="text-align:center;margin:0 0 14px;font-size:12px">Orden #${orden.id}</p>
      <table style="width:100%;border-collapse:collapse">
        ${fila("Fecha entrada", orden.fecha)}
        ${fila("Chasis", orden.chasis)}
        ${fila("Vehículo", [orden.marca, orden.modelo, orden.color].filter(Boolean).join(" · "))}
        ${fila("Responsable", orden.responsable)}
        ${fila("Total gastos", `RD$ ${fmtMoney(totalgastos)}`)}
        ${fila("Pagado por cliente", `RD$ ${fmtMoney(pagadocliente)}`)}
        ${fila("Destino", destino === "fabrica" ? "Devuelto a fábrica" : "Volver al inventario")}
      </table>
      <p style="margin-top:34px;border-top:1px solid #000;padding-top:6px;text-align:center">Firma</p>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      /* noop */
    }
    setTimeout(() => iframe.remove(), 60000);
  }, 200);
}

// Comprobante consolidado de la salida múltiple (misma técnica de iframe).
function imprimirSalidaMultiple({ resultados, destino }) {
  const filas = resultados
    .map(
      (r) =>
        `<tr>
          <td style="padding:3px 6px;border-bottom:1px solid #ddd">#${r.id}</td>
          <td style="padding:3px 6px;border-bottom:1px solid #ddd">${r.chasis || ""}</td>
          <td style="padding:3px 6px;border-bottom:1px solid #ddd">${[r.marca, r.modelo]
            .filter(Boolean)
            .join(" ")}</td>
          <td style="padding:3px 6px;border-bottom:1px solid #ddd;text-align:right">${fmtMoney(
            r.totalgastos
          )}</td>
          <td style="padding:3px 6px;border-bottom:1px solid #ddd;text-align:right">${fmtMoney(
            r.pagadocliente
          )}</td>
        </tr>`
    )
    .join("");
  const totGastos = resultados.reduce((a, r) => a + (parseFloat(r.totalgastos) || 0), 0);
  const totPagado = resultados.reduce((a, r) => a + (parseFloat(r.pagadocliente) || 0), 0);
  const html = `
    <html><head><meta name="color-scheme" content="light"><title>Salida de Taller (${
      resultados.length
    } ordenes)</title></head>
    <body style="background:#fff;color:#000;font-family:Arial,sans-serif;padding:18px;font-size:13px;max-width:640px;margin:0 auto">
      <p style="text-align:center;margin:0 0 4px;font-weight:bold">Salida de Taller</p>
      <p style="text-align:center;margin:0 0 4px;font-size:12px">${
        resultados.length
      } órdenes · ${new Date().toLocaleDateString("es-DO")}</p>
      <p style="text-align:center;margin:0 0 14px;font-size:12px">Destino: ${
        destino === "fabrica" ? "Devuelto a fábrica" : "Volver al inventario"
      }</p>
      <table style="width:100%;border-collapse:collapse">
        <tr>
          <th style="padding:3px 6px;border-bottom:1px solid #000;text-align:left">Orden</th>
          <th style="padding:3px 6px;border-bottom:1px solid #000;text-align:left">Chasis</th>
          <th style="padding:3px 6px;border-bottom:1px solid #000;text-align:left">Vehículo</th>
          <th style="padding:3px 6px;border-bottom:1px solid #000;text-align:right">Gastos RD$</th>
          <th style="padding:3px 6px;border-bottom:1px solid #000;text-align:right">Pagado RD$</th>
        </tr>
        ${filas}
        <tr>
          <td colspan="3" style="padding:5px 6px;font-weight:bold">Totales</td>
          <td style="padding:5px 6px;font-weight:bold;text-align:right">${fmtMoney(totGastos)}</td>
          <td style="padding:5px 6px;font-weight:bold;text-align:right">${fmtMoney(totPagado)}</td>
        </tr>
      </table>
      <p style="margin-top:34px;border-top:1px solid #000;padding-top:6px;text-align:center">Firma</p>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      /* noop */
    }
    setTimeout(() => iframe.remove(), 60000);
  }, 200);
}

export default function SalidaTaller({ embedded = false, onExit, onSaved } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [chasis, setChasis] = useState("");
  const [buscando, setBuscando] = useState(false);
  const [orden, setOrden] = useState(null);
  const [gastos, setGastos] = useState([]);
  const [pagadocliente, setPagadocliente] = useState("");
  const [destino, setDestino] = useState("inventario");
  const [confirmar, setConfirmar] = useState(false);
  const [guardando, setGuardando] = useState(false);

  // Modo "varios motores": órdenes abiertas con selección múltiple.
  const [modo, setModo] = useState("individual");
  const [ordenesAbiertas, setOrdenesAbiertas] = useState([]);
  const [cargandoOrdenes, setCargandoOrdenes] = useState(false);
  const [seleccion, setSeleccion] = useState([]);
  const [filtroChasis, setFiltroChasis] = useState("");
  const [confirmarMulti, setConfirmarMulti] = useState(false);
  const [guardandoMulti, setGuardandoMulti] = useState(false);

  const cargarOrdenesAbiertas = useCallback(async () => {
    setCargandoOrdenes(true);
    try {
      const { ok, data } = await post("/api/taller_ordenes_abiertas", {});
      if (ok) {
        setOrdenesAbiertas((data.ordenes || []).map((o) => ({ ...o, pagadocliente: 0 })));
        setSeleccion([]);
      } else {
        showAlert({
          message: data.Error || "No se pudieron cargar las órdenes abiertas",
          color: "error",
        });
      }
    } finally {
      setCargandoOrdenes(false);
    }
  }, [post, showAlert]);

  useEffect(() => {
    if (modo === "multiple") cargarOrdenesAbiertas();
  }, [modo, cargarOrdenesAbiertas]);

  // Filtro por chasis/marca/modelo sobre el listado; la selección de filas
  // ocultas por el filtro se conserva (keepNonExistentRowsSelected).
  const filtroTxt = filtroChasis.trim().toUpperCase();
  const ordenesFiltradas = filtroTxt
    ? ordenesAbiertas.filter((o) =>
        `${o.chasis} ${o.marca || ""} ${o.modelo || ""}`.toUpperCase().includes(filtroTxt)
      )
    : ordenesAbiertas;

  const seleccionadas = ordenesAbiertas.filter((o) => seleccion.includes(o.id));
  const totalGastosSel = seleccionadas.reduce((a, o) => a + (parseFloat(o.totalgastos) || 0), 0);
  const totalPagadoSel = seleccionadas.reduce((a, o) => a + (parseFloat(o.pagadocliente) || 0), 0);

  const registrarSalidaMultiple = async () => {
    setGuardandoMulti(true);
    try {
      const { ok, data } = await post("/api/taller_salida_multiple", {
        ordenes: seleccionadas.map((o) => ({
          id: o.id,
          pagadocliente: parseFloat(o.pagadocliente) || 0,
        })),
        destino,
      });
      if (ok) {
        showAlert({
          message: data.ok || `Salida registrada para ${seleccionadas.length} órdenes`,
          color: "success",
        });
        setConfirmarMulti(false);
        imprimirSalidaMultiple({ resultados: data.resultados || [], destino });
        cargarOrdenesAbiertas();
        if (onSaved) onSaved();
      } else {
        showAlert({ message: data.Error || "No se pudo registrar la salida", color: "error" });
      }
    } finally {
      setGuardandoMulti(false);
    }
  };

  const limpiar = () => {
    setChasis("");
    setOrden(null);
    setGastos([]);
    setPagadocliente("");
    setDestino("inventario");
  };

  const buscar = async () => {
    if (!chasis.trim()) {
      showAlert({ message: "Debe indicar el chasis", color: "warning" });
      return;
    }
    setBuscando(true);
    try {
      const { ok, data } = await post("/api/taller_orden_datos", { chasis: chasis.trim() });
      if (ok && data.orden) {
        setOrden(data.orden);
        setGastos((data.gastos || []).map((g, i) => ({ ...g, id: i })));
        setPagadocliente("");
        setDestino("inventario");
      } else {
        setOrden(null);
        setGastos([]);
        showAlert({
          message: data.Error || "No se encontró una orden de taller para ese chasis",
          color: "error",
        });
      }
    } finally {
      setBuscando(false);
    }
  };

  const registrarSalida = async () => {
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/taller_salida", {
        id: orden.id,
        pagadocliente: parseFloat(pagadocliente) || 0,
        destino,
      });
      if (ok) {
        showAlert({ message: data.mensajeok || "Salida registrada", color: "success" });
        setConfirmar(false);
        imprimirSalida({
          orden,
          totalgastos: data.totalgastos ?? orden.totalgastos,
          pagadocliente: parseFloat(pagadocliente) || 0,
          destino,
        });
        limpiar();
        if (onSaved) onSaved();
      } else {
        showAlert({ message: data.Error || "No se pudo registrar la salida", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  const columnsMulti = [
    { field: "id", headerName: "Orden", width: 90 },
    { field: "fecha", headerName: "Fecha", width: 100 },
    { field: "chasis", headerName: "Chasis", width: 180 },
    { field: "marca", headerName: "Marca", width: 120 },
    { field: "modelo", headerName: "Modelo", width: 130 },
    { field: "responsable", headerName: "Responsable", flex: 1, minWidth: 140 },
    { field: "sucursal", headerName: "Suc.", width: 70 },
    {
      field: "totalgastos",
      headerName: "Gastos",
      width: 110,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "pagadocliente",
      headerName: "Pagado cliente",
      width: 130,
      align: "right",
      headerAlign: "right",
      type: "number",
      editable: true,
      valueFormatter: (value) => fmtMoney(value),
    },
  ];

  const columns = [
    { field: "fecha", headerName: "Fecha", width: 110 },
    { field: "descripcion", headerName: "Descripción", flex: 1, minWidth: 200 },
    { field: "tipogastos", headerName: "Tipo", width: 150 },
    {
      field: "valor",
      headerName: "Valor",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
  ];

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Salida Motor Taller"
        subtitle="Cierra la orden de taller: registra lo pagado por el cliente y el destino del vehículo"
        cta={
          <Stack direction="row" spacing={1.2}>
            {embedded && (
              <Button startIcon={<ArrowBackIcon />} onClick={onExit} sx={btnOutline(FP.color.navy)}>
                Volver
              </Button>
            )}
            {modo === "individual" && (
              <Button
                startIcon={<RestartAltIcon />}
                onClick={limpiar}
                sx={btnOutline(FP.color.navy)}
              >
                Nuevo
              </Button>
            )}
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Selector de modo: una orden por chasis o varias a la vez */}
        <Stack direction="row" spacing={1.2} sx={{ mb: 2.4 }}>
          <Button
            startIcon={<TwoWheelerIcon />}
            onClick={() => setModo("individual")}
            sx={
              modo === "individual"
                ? btnSolid(FP.color.navy, FP.color.navySoft)
                : btnOutline(FP.color.navy)
            }
          >
            Un motor
          </Button>
          <Button
            startIcon={<LibraryAddCheckOutlinedIcon />}
            onClick={() => setModo("multiple")}
            sx={
              modo === "multiple"
                ? btnSolid(FP.color.navy, FP.color.navySoft)
                : btnOutline(FP.color.navy)
            }
          >
            Varios motores
          </Button>
        </Stack>

        {modo === "individual" && (
          <>
            {/* Búsqueda de la orden */}
            <Box sx={sectionBox}>
              <SectionTitle icon={BuildOutlinedIcon} color={FP.color.accentBlue}>
                Orden de Taller
              </SectionTitle>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={5} md={4}>
                  <TextField
                    fullWidth
                    label="Chasis"
                    value={chasis}
                    onChange={(e) => setChasis(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === "Enter" && buscar()}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={3} md={2}>
                  <Button
                    startIcon={
                      buscando ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                    }
                    onClick={buscar}
                    disabled={buscando}
                    sx={btnSolid(FP.color.navy, FP.color.navySoft)}
                  >
                    Buscar
                  </Button>
                </Grid>
                <Grid item xs={12} md={6} sx={{ display: "flex", alignItems: "center" }}>
                  {orden && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip
                        label={`Orden #${orden.id} · ${orden.fecha}`}
                        sx={{ bgcolor: "#E3F2FD", color: "#1565C0", fontWeight: 700 }}
                      />
                      {[orden.marca, orden.modelo, orden.color].filter(Boolean).length > 0 && (
                        <Chip
                          label={[orden.marca, orden.modelo, orden.color]
                            .filter(Boolean)
                            .join(" · ")}
                          sx={{
                            bgcolor: FP.color.surfaceAlt,
                            color: FP.color.textSecondary,
                            fontWeight: 700,
                          }}
                        />
                      )}
                      {orden.responsable && (
                        <Chip
                          label={`Responsable: ${orden.responsable}`}
                          sx={{
                            bgcolor: FP.color.surfaceAlt,
                            color: FP.color.textSecondary,
                            fontWeight: 700,
                          }}
                        />
                      )}
                      {orden.cliente && (
                        <Chip
                          label={`Cliente: ${orden.cliente}`}
                          sx={{
                            bgcolor: FP.color.surfaceAlt,
                            color: FP.color.textSecondary,
                            fontWeight: 700,
                          }}
                        />
                      )}
                      {orden.estadovehiculo && (
                        <Chip
                          label={`Estado: ${orden.estadovehiculo}`}
                          sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
                        />
                      )}
                    </Stack>
                  )}
                </Grid>
                {orden?.danofisico && (
                  <Grid item xs={12}>
                    <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary }}>
                      <b>Daño físico:</b> {orden.danofisico}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>

            {orden && (
              <>
                {/* Gastos */}
                <Box sx={sectionBox}>
                  <SectionTitle icon={ReceiptLongOutlinedIcon} color={FP.color.success}>
                    Gastos de la Orden
                  </SectionTitle>
                  <Box sx={{ bgcolor: "#fff" }}>
                    <DataGrid
                      autoHeight
                      rows={gastos}
                      columns={columns}
                      rowHeight={42}
                      hideFooter={gastos.length <= 25}
                      pageSizeOptions={[25, 50]}
                      initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                      slots={{ noRowsOverlay: NoRowsOverlay }}
                      sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "180px" }}
                      disableRowSelectionOnClick
                    />
                  </Box>
                  <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.2 }}>
                    <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary }}>
                      Total gastos: RD$ {fmtMoney(orden.totalgastos)}
                    </Typography>
                  </Stack>
                </Box>

                {/* Salida */}
                <Box sx={{ ...sectionBox, mb: 0 }}>
                  <SectionTitle icon={LogoutOutlinedIcon} color={FP.color.navy}>
                    Registrar Salida
                  </SectionTitle>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4} md={3}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Pagado por cliente"
                        value={pagadocliente}
                        onChange={(e) => setPagadocliente(e.target.value)}
                        inputProps={{ min: 0, step: "0.01" }}
                        sx={fpInputSx}
                      />
                    </Grid>
                    <Grid item xs={12} sm={8} md={6}>
                      <RadioGroup row value={destino} onChange={(e) => setDestino(e.target.value)}>
                        <FormControlLabel
                          value="inventario"
                          control={<Radio size="small" />}
                          label="Volver al inventario"
                        />
                        <FormControlLabel
                          value="fabrica"
                          control={<Radio size="small" />}
                          label="Devuelto a fábrica"
                        />
                      </RadioGroup>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Button
                        fullWidth
                        startIcon={<LogoutOutlinedIcon />}
                        onClick={() => setConfirmar(true)}
                        sx={btnSolid(FP.color.navy, FP.color.navySoft)}
                      >
                        Registrar salida
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </>
            )}
          </>
        )}

        {modo === "multiple" && (
          <Box sx={{ ...sectionBox, mb: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <SectionTitle icon={LibraryAddCheckOutlinedIcon} color={FP.color.accentBlue}>
                Órdenes Abiertas
              </SectionTitle>
              <Button
                startIcon={
                  cargandoOrdenes ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />
                }
                onClick={cargarOrdenesAbiertas}
                disabled={cargandoOrdenes}
                sx={btnOutline(FP.color.navy)}
              >
                Recargar
              </Button>
            </Box>
            <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary, mb: 1.6 }}>
              Marque las órdenes a dar salida. El <b>pagado por cliente</b> se edita con doble clic
              en la celda; el destino aplica a todas las seleccionadas.
            </Typography>
            <Grid container spacing={2} sx={{ mb: 1.6 }}>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  freeSolo
                  autoHighlight
                  clearOnEscape
                  options={ordenesAbiertas}
                  filterOptions={filtrarOpcionesOrdenes}
                  inputValue={filtroChasis}
                  onInputChange={(_, v) => setFiltroChasis(v || "")}
                  getOptionLabel={(o) => (typeof o === "string" ? o : o.chasis || "")}
                  renderOption={(props, o) => (
                    <li {...props} key={o.id}>
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                          {o.chasis}
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                          {[o.marca, o.modelo].filter(Boolean).join(" · ") || `Orden #${o.id}`}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Filtrar por chasis, marca o modelo"
                      sx={fpInputSx}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Box sx={{ bgcolor: "#fff" }}>
              <DataGrid
                autoHeight
                rows={ordenesFiltradas}
                columns={columnsMulti}
                rowHeight={42}
                loading={cargandoOrdenes}
                checkboxSelection
                disableRowSelectionOnClick
                keepNonExistentRowsSelected
                rowSelectionModel={seleccion}
                onRowSelectionModelChange={(modelo) => setSeleccion(modelo)}
                processRowUpdate={(newRow) => {
                  const fila = {
                    ...newRow,
                    pagadocliente: Math.max(0, parseFloat(newRow.pagadocliente) || 0),
                  };
                  setOrdenesAbiertas((prev) => prev.map((r) => (r.id === fila.id ? fila : r)));
                  return fila;
                }}
                hideFooter={ordenesFiltradas.length <= 25}
                pageSizeOptions={[25, 50, 100]}
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                slots={{
                  noRowsOverlay:
                    filtroTxt && ordenesAbiertas.length
                      ? NoRowsOverlaySinCoincidencias
                      : NoRowsOverlaySinOrdenes,
                }}
                sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "180px" }}
              />
            </Box>
            <Grid container spacing={2} alignItems="center" sx={{ mt: 0.4 }}>
              <Grid item xs={12} md={5}>
                <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary }}>
                  {seleccion.length} seleccionadas · Gastos: RD$ {fmtMoney(totalGastosSel)} ·
                  Pagado: RD$ {fmtMoney(totalPagadoSel)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8} md={4}>
                <RadioGroup row value={destino} onChange={(e) => setDestino(e.target.value)}>
                  <FormControlLabel
                    value="inventario"
                    control={<Radio size="small" />}
                    label="Volver al inventario"
                  />
                  <FormControlLabel
                    value="fabrica"
                    control={<Radio size="small" />}
                    label="Devuelto a fábrica"
                  />
                </RadioGroup>
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <Button
                  fullWidth
                  startIcon={<LogoutOutlinedIcon />}
                  onClick={() => setConfirmarMulti(true)}
                  disabled={seleccion.length === 0}
                  sx={btnSolid(FP.color.navy, FP.color.navySoft)}
                >
                  Registrar salida ({seleccion.length})
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>

      {/* Confirmación de salida múltiple */}
      <Dialog
        open={confirmarMulti}
        onClose={() => setConfirmarMulti(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.navy }}>
          Registrar salida de {seleccion.length} órdenes
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.9rem" }}>
            Se dará salida a <b>{seleccion.length}</b> órdenes con gastos por RD${" "}
            <b>{fmtMoney(totalGastosSel)}</b> y pagado por cliente RD${" "}
            <b>{fmtMoney(totalPagadoSel)}</b>. Los vehículos{" "}
            {destino === "fabrica"
              ? "quedarán marcados como devueltos a fábrica."
              : "volverán al inventario disponible."}{" "}
            Si alguna orden falla, no se registra ninguna.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmarMulti(false)} sx={btnGhost}>
            No
          </Button>
          <Button
            startIcon={
              guardandoMulti ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <LogoutOutlinedIcon />
              )
            }
            onClick={registrarSalidaMultiple}
            disabled={guardandoMulti}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Sí, registrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación de salida */}
      <Dialog
        open={confirmar}
        onClose={() => setConfirmar(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.navy }}>
          Registrar salida de taller
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.9rem" }}>
            Orden <b>#{orden?.id}</b> del chasis <b>{orden?.chasis}</b>. Gastos por RD${" "}
            <b>{fmtMoney(orden?.totalgastos)}</b>, pagado por el cliente RD${" "}
            <b>{fmtMoney(parseFloat(pagadocliente) || 0)}</b>. El vehículo{" "}
            {destino === "fabrica"
              ? "quedará marcado como devuelto a fábrica."
              : "volverá al inventario disponible."}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmar(false)} sx={btnGhost}>
            No
          </Button>
          <Button
            startIcon={
              guardando ? <CircularProgress size={16} color="inherit" /> : <LogoutOutlinedIcon />
            }
            onClick={registrarSalida}
            disabled={guardando}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Sí, registrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
